import LoadingButton from '@mui/lab/LoadingButton'
import {
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CardSection } from './CardSection'
import { LoadingText } from './LoadingText'
import { getPrice } from './prices'
import { DataType } from './types'

function QuantityRow({
  name,
  value,
  bold,
}: {
  name: string
  value: string
  bold?: boolean
}) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body1" fontWeight={bold ? 700 : undefined}>
        {name}
      </Typography>
      <Typography
        variant="body1"
        fontWeight={bold ? 700 : undefined}
      >{`${value}`}</Typography>
    </Stack>
  )
}

function App() {
  const cards = JSON.parse(sessionStorage.getItem('cards') || '[]')
  const [loading, setLoading] = useState(false)
  const [text, setText] = useState('')
  const [data, setData] = useState<DataType>({
    cards,
    errors: [],
    showErrors: true,
  })

  useEffect(() => {
    if (cards.length > 0) {
      const cartItem: any = document.getElementsByClassName('cart-contents')[0]
      cartItem.click()

      sessionStorage.removeItem('cards')
    }
  }, [cards])

  const handleSubmit = async () => {
    const cards = text.split('\n')
    const response = await fetch(
      'https://printmtg-server.onrender.com/card-list',
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cards }),
      }
    ).then((response) => response.json())
    setData({
      cards: [...data.cards, ...response.cards],
      errors: response.errors,
      showErrors: true,
    })
    setText('')
  }

  const addToCart = async (quantity: number, orderID: number) => {
    const formData = new FormData()
    formData.append('add-to-cart', '2135')
    formData.append('quantity', quantity.toString())
    formData.append('card-list', orderID.toString())

    await fetch('/print-proxies/', {
      method: 'POST',
      body: formData,
    })
    await location.reload()
  }

  const generateOrder = async (cardList: DataType) => {
    setLoading(true)
    const response = await fetch('https://printmtg-server.onrender.com/order', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cardList: cardList.cards.map((card) => ({
          name: card.name,
          quantity: card.quantity,
          imageUrl: card.selectedURL,
        })),
      }),
    }).then((response) => response.json())
    const orderID = response.orderID
    const quantity = data.cards.reduce((acc, card) => acc + card.quantity, 0)
    sessionStorage.setItem('cards', JSON.stringify(cardList.cards))

    await addToCart(quantity, orderID)
  }

  const quantity = data.cards.reduce((acc, card) => acc + card.quantity, 0)
  const pricePer = getPrice(quantity)

  const showLoading =
    !data.cards.length && !(data.showErrors && data.errors.length)

  return (
    <Container sx={{ pt: 1, pb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Stack spacing={2}>
            <Stack>
              <TextField
                value={text}
                onChange={(e) => setText(e.target.value)}
                multiline
                fullWidth
                rows={15}
                placeholder="4 Sol Ring&#10;2 Swords to Plowshares&#10;&#10;2x Black Lotus&#10;Squee Goblin Nabob&#10;2 Cyclonic Rift&#10;3x Rough / Tumble&#10;Dead / Gone&#10;4x Terminate&#10;// Sideboard&#10;Heroic Intervention&#10;2x Brainstorm&#10;SB: Birds of Paradise&#10;"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={text.length < 3}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                Add Cards
              </Button>
            </Stack>
            <LoadingButton
              loading={loading}
              onClick={() => generateOrder(data)}
              disabled={data.cards.length < 1}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Create Order
            </LoadingButton>
            <Stack>
              <QuantityRow name="Total Quantity" value={quantity.toString()} />
              <QuantityRow
                name="Price Per Card"
                value={quantity > 0 ? `$${pricePer}` : '-'}
              />
              <QuantityRow
                name="Total Price"
                value={`$${quantity * pricePer}`}
                bold
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={9}>
          {showLoading ? (
            <LoadingText />
          ) : (
            <CardSection data={data} setData={setData} />
          )}
        </Grid>
      </Grid>
    </Container>
  )
}

export default App
